import { create } from "zustand";
import { createJSONStorage, persist, type StateStorage } from "zustand/middleware";

import type { SmoothingPreset } from "@/components/reader/ink";

/** The active markup tool. "none" = reading (read-only SVG overlay, no Fabric
 * loaded); anything else = editing mode (Fabric overlay mounts). */
export type AnnotationTool =
  | "none"
  | "select"
  | "pen"
  | "highlighter"
  | "text"
  | "eraser"
  | "lasso"
  /** The paint bucket: one tap floods the enclosed area under it. */
  | "fill"
  | ShapeTool;

/** The drawn shapes. Each is its own tool (not a "shape" tool plus a separate
 * kind) so the editor, the digit shortcuts, and the persisted annotation type
 * all key off ONE value. All ten share the toolbar's shape button — see
 * {@link lastShape} — because nine more icons would break the island's
 * fixed-width layout invariant. */
export type ShapeTool =
  | "rect"
  | "ellipse"
  | "diamond"
  | "line"
  | "arrow"
  // The rest of a flowchart's vocabulary.
  | "parallelogram"
  | "terminator"
  | "hexagon"
  | "cylinder"
  // A ruled table, sized from the pen (see the editor's tableDraft).
  | "table";

export const SHAPE_TOOLS: ShapeTool[] = [
  "rect",
  "ellipse",
  "diamond",
  "line",
  "arrow",
  "parallelogram",
  "terminator",
  "hexagon",
  "cylinder",
  "table",
];

/** Digit shortcuts for the shapes. 1–6 belong to the core tools and 7 opens/
 * cycles the shape button, which left 8, 9 and 0 free — so the first three
 * shapes get one each, for a workflow that reaches for them constantly. The
 * remaining seven stay on 7's cycle and the palette. */
export const SHAPE_DIGITS: Partial<Record<ShapeTool, string>> = {
  rect: "8",
  ellipse: "9",
  diamond: "0",
};

const SHAPE_SET = new Set<string>(SHAPE_TOOLS);

export function isShapeTool(t: AnnotationTool): t is ShapeTool {
  return SHAPE_SET.has(t);
}

/** Annotation geometry is stored page-RELATIVE ([0,1] of the page) so markup is
 * invariant to zoom, DPI, and wildly different document sizes. But people think
 * in POINTS (like Word / Google Docs), so the toolbar shows a point number and
 * converts to/from a fraction here, using a US-Letter reference page (612×792
 * pt). The same number therefore renders proportionally on any document — small
 * or huge — and free numeric entry gives effectively unlimited variance. */
export const PAGE_REF_W = 612; // US-Letter width in points  → pen-width reference
export const PAGE_REF_H = 792; // US-Letter height in points → font-size reference

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

// Backend bounds (server/.../domain/annotations.py): font_size_rel ∈ [0,1],
// stroke_rel ∈ [0,0.2]. The converters clamp into those so any typed number is
// safe to persist.
export const fontPtToRel = (pt: number) => clamp((Number(pt) || 0) / PAGE_REF_H, 0.001, 1);
export const relToFontPt = (rel: number) => Math.round(rel * PAGE_REF_H);
export const strokePtToRel = (pt: number) => clamp((Number(pt) || 0) / PAGE_REF_W, 0.0008, 0.2);
export const relToStrokePt = (rel: number) => Math.round(rel * PAGE_REF_W * 4) / 4; // 0.25-pt steps

/** Suggested sizes for the pickers — a Word/GDocs-style spread. Free numeric
 * entry is also allowed, so these are only shortcuts, not a cap. */
export const FONT_SIZE_POINTS = [6, 8, 9, 10, 11, 12, 14, 18, 24, 30, 36, 48, 60, 72, 96, 144];
export const PEN_WIDTH_POINTS = [0.5, 1, 1.5, 2, 3, 4, 6, 8, 12, 18, 24, 36];
// Highlighters are broad and chunky — a thicker spread than the pen.
export const HIGHLIGHTER_WIDTH_POINTS = [6, 8, 12, 16, 20, 24, 30, 36, 48];
// Eraser hit radius, in the same point space as a pen width.
export const ERASER_RADIUS_POINTS = [4, 6, 8, 12, 16, 24, 36, 48];

/** Whole-stroke vs. area erasing. "stroke" removes any stroke the eraser
 * touches; "area" removes only the span under the eraser, splitting the stroke
 * into the pieces that survive (GoodNotes-style). */
export type EraseMode = "stroke" | "area";

// --- the curated colour palette (the swatches kept on the toolbar) ----------
// A small set the user pins for one-tap reuse, persisted across sessions (via
// `partialize` under the store's own key). The colour picker still allows any
// colour; "keep" adds it here.
const DEFAULT_PALETTE = [
  "#111111", // ink black
  "#e23b3b", // red
  "#2f6df6", // blue
  "#1f9d55", // green
  "#f5a623", // amber
  "#9b51e0", // violet
  "#e7e5da", // paper (light, for dark pages)
];
const MAX_PALETTE = 16;

const normHex = (c: string) => c.trim().toLowerCase();

// --- adaptive ink + the three quick-colour slots ----------------------------

/** Sentinel for paper-adaptive ink, stored rather than resolved so a pen keeps
 * FOLLOWING the paper it is used on. Resolving at pick time would freeze white
 * ink into a note that later turns light (and vice versa). */
export const ADAPTIVE_INK = "adaptive";

/** What {@link ADAPTIVE_INK} resolves to. Not pure #000/#fff — a hair off-white
 * reads as chalk on a dark page rather than glare. */
export const INK_ON_LIGHT = "#111111";
export const INK_ON_DARK = "#e7e5da";

/** A stored colour → the colour to actually draw with on this paper. */
export function resolveInk(color: string, paper: "light" | "dark"): string {
  if (color !== ADAPTIVE_INK) return color;
  return paper === "dark" ? INK_ON_DARK : INK_ON_LIGHT;
}

/** Which tools keep their own colour slots. A highlighter's palette has nothing
 * to do with a pen's — you do not want to hunt for yellow after switching. Any
 * other tool (text, select) borrows the pen's. */
export type InkSlotTool = "pen" | "highlighter";

export const slotToolFor = (tool: AnnotationTool): InkSlotTool =>
  tool === "highlighter" ? "highlighter" : "pen";

/** The colours kept under the thumb on the toolbar, per ink tool. The pen's
 * slot 0 defaults to adaptive ink — the "just write" pen that is black on paper
 * and white on a dark page; the highlighter starts on transparent-friendly
 * brights. Three each to begin with, growable up to {@link MAX_COLOR_SLOTS}. */
export const DEFAULT_COLOR_SLOTS: Record<InkSlotTool, string[]> = {
  pen: [ADAPTIVE_INK, "#e23b3b", "#2f6df6"],
  highlighter: ["#f7e463", "#7ee081", "#f79ac0"],
};

/** Slots are added by hand, so the cap only exists to stop the row growing
 * without bound — the toolbar reserves no space for them. */
export const MAX_COLOR_SLOTS = 6;

/** A slot holds either the adaptive sentinel or a hex colour; anything else
 * came from a corrupt or hand-edited store and is refused. */
export function isStorableColor(value: unknown): value is string {
  return (
    value === ADAPTIVE_INK ||
    (typeof value === "string" && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value))
  );
}

interface AnnotationToolState {
  tool: AnnotationTool;
  /** The last non-"none" ink tool chosen, remembered across sessions so
   *  re-entering annotate mode restores it. The live `tool` stays read-mode on
   *  open, so a document never opens mid-draw. */
  lastTool: AnnotationTool;
  /** The shape the toolbar's single shape button currently offers — the last
   * one picked, so the button keeps showing (and one tap re-selects) the shape
   * you have been drawing. All ten shapes share one slot because the island's
   * width is a hard layout invariant. */
  lastShape: ShapeTool;
  /** Shape styling, applied to the NEXT shape drawn (and to a selected one).
   * Outline-only, solid, square corners is the default — the figure a shape has
   * always been — so turning these on is always an addition, never a surprise. */
  shapeFill: string | null;
  shapeDash: boolean;
  /** Rect corner rounding, a fraction of the box's short side. */
  shapeCorner: number;
  /** The floating shape palette (opened from the ⋯ menu) is showing. It is a
   * standing surface for a shape-heavy workflow, so it persists — closing it is
   * a deliberate act, not something a refresh should do for you. */
  shapePaletteOpen: boolean;
  color: string;
  fontFamily: string;
  /** font_size_rel — a fraction of page height. UI shows relToFontPt(). */
  fontSizeRel: number;
  /** stroke_rel — a fraction of page width. UI shows relToStrokePt(). */
  strokeRel: number;
  /** highlighter width — its own fraction of page width (broad + uniform). */
  highlighterRel: number;
  /** eraser hit radius — a fraction of page width. */
  eraserRel: number;
  /** Whole-stroke vs. area (partial) erasing. */
  eraseMode: EraseMode;
  /** Stylus pressure → variable stroke width (Apple Pencil etc.). */
  pressure: boolean;
  /** Pen-input smoothing preset (§3.7) — maps to perfect-freehand params in
   * ink.ts. A session preference: saved strokes keep raw points, no preset. */
  smoothing: SmoothingPreset;
  /** Ignore finger/palm touches while a pen is active (Apple-style). */
  palmReject: boolean;
  /** The pinned colour swatches kept on the toolbar (curated by the user). */
  palette: string[];
  /** The quick-colour slots on the toolbar, per ink tool. A slot may hold
   * {@link ADAPTIVE_INK}; callers resolve it against the paper. */
  colorSlots: Record<InkSlotTool, string[]>;
  /** The colour each ink tool was last left on, so switching pen <-> highlighter
   * restores that tool's own ink rather than carrying the other's across. */
  toolColor: Record<InkSlotTool, string>;
  setTool: (tool: AnnotationTool) => void;
  setColor: (color: string) => void;
  setEraseMode: (mode: EraseMode) => void;
  /** Pin the current (or a given) colour onto the palette. */
  addPaletteColor: (color: string) => void;
  /** Remove a colour from the palette (keeps at least one). */
  removePaletteColor: (color: string) => void;
  /** Point one of a tool's slots at a colour (or the adaptive ink). */
  setColorSlot: (tool: InkSlotTool, index: number, color: string) => void;
  /** Append a new custom colour slot for a tool (capped at MAX_COLOR_SLOTS).
   * Returns silently when full or when the colour is unusable. */
  addColorSlot: (tool: InkSlotTool, color: string) => void;
  setFont: (fontFamily: string) => void;
  setFontSize: (fontSizeRel: number) => void;
  setStrokeWidth: (strokeRel: number) => void;
  setHighlighterWidth: (highlighterRel: number) => void;
  setEraserRadius: (eraserRel: number) => void;
  setPressure: (pressure: boolean) => void;
  setSmoothing: (smoothing: SmoothingPreset) => void;
  setPalmReject: (palmReject: boolean) => void;
  setShapePaletteOpen: (open: boolean) => void;
  setShapeFill: (color: string | null) => void;
  setShapeDash: (dash: boolean) => void;
  setShapeCorner: (radius: number) => void;
  /** Leave editing mode (e.g. when the reader closes). */
  reset: () => void;
}

/** `createJSONStorage(() => localStorage)` with the WRITE coalesced onto a trailing
 *  timer. Reads stay synchronous and immediate, so rehydration is unaffected.
 *
 *  A pending write is flushed on `pagehide` and on a hide, which covers a reload, a
 *  tab close and a mobile app switch. Anything the browser kills without either is
 *  at most one interval of preference changes, which is the right trade for taking a
 *  synchronous serialize off every keystroke of a colour drag. */
function debouncedLocalStorage(delayMs = 250): StateStorage {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let pending: { name: string; value: string } | null = null;

  const flush = () => {
    if (timer !== undefined) {
      clearTimeout(timer);
      timer = undefined;
    }
    if (!pending) return;
    const { name, value } = pending;
    pending = null;
    try {
      localStorage.setItem(name, value);
    } catch {
      /* private mode, or the quota is full: a preference is not worth a throw */
    }
  };

  if (typeof window !== "undefined") {
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flush();
    });
  }

  return {
    getItem: (name: string) => {
      // A read must see a write that has not landed yet, or a rehydrate during the
      // window would resurrect the previous value.
      if (pending && pending.name === name) return pending.value;
      try {
        return localStorage.getItem(name);
      } catch {
        return null;
      }
    },
    setItem: (name: string, value: string) => {
      pending = { name, value };
      if (timer !== undefined) clearTimeout(timer);
      timer = setTimeout(flush, delayMs);
    },
    removeItem: (name: string) => {
      if (pending?.name === name) pending = null;
      if (timer !== undefined) {
        clearTimeout(timer);
        timer = undefined;
      }
      try {
        localStorage.removeItem(name);
      } catch {
        /* ignore */
      }
    },
  };
}

/** One instance, so every access shares the same pending write and timer. */
const DEBOUNCED_STORAGE = debouncedLocalStorage();

export const useAnnotationTool = create<AnnotationToolState>()(
  persist(
    (set) => ({
      tool: "none",
      // Remembered across sessions; the live `tool` itself opens read-mode.
      lastTool: "pen",
      lastShape: "rect",
      shapeFill: null,
      shapeDash: false,
      shapeCorner: 0,
      shapePaletteOpen: false,
  // Default ink/text color is black (a pen on paper), not the brass accent.
  color: "#111111",
  // Typed text defaults to MONO. A text annotation sits beside handwriting, and
  // a monospace face is the one that reads as deliberately typed rather than as
  // a failed imitation of the pen next to it.
  fontFamily: "mono",
  fontSizeRel: fontPtToRel(24), // ≈ 24 pt
  strokeRel: strokePtToRel(2.5), // ≈ 2.5 pt
  highlighterRel: strokePtToRel(16), // ≈ 16 pt — broad marker
  eraserRel: strokePtToRel(12), // ≈ 12 pt radius
  eraseMode: "stroke",
  pressure: true,
  smoothing: "balanced",
  palmReject: true,
  palette: DEFAULT_PALETTE,
  colorSlots: DEFAULT_COLOR_SLOTS,
  toolColor: { pen: "#111111", highlighter: "#f7e463" },
  // Switching between the pen and the highlighter restores that tool's own
  // ink — a highlighter should not come back holding the pen's black.
  setTool: (tool) =>
    set((s) => {
      if (tool === "none") return { tool };
      const next = slotToolFor(tool);
      const carry =
        tool === "pen" || tool === "highlighter" ? { color: s.toolColor[next] } : {};
      // Picking a shape also makes it the one the toolbar's shape button offers.
      const shape = isShapeTool(tool) ? { lastShape: tool } : {};
      return { tool, lastTool: tool, ...carry, ...shape };
    }),
  setColor: (color) =>
    set((s) => ({
      color,
      toolColor: { ...s.toolColor, [slotToolFor(s.tool)]: color },
    })),
  setEraseMode: (eraseMode) => set({ eraseMode }),
  addPaletteColor: (color) =>
    set((s) => {
      const c = normHex(color);
      if (s.palette.some((p) => normHex(p) === c)) return { color: c };
      const palette = [...s.palette, c].slice(-MAX_PALETTE);
      return { palette, color: c };
    }),
  removePaletteColor: (color) =>
    set((s) => {
      const c = normHex(color);
      const palette = s.palette.filter((p) => normHex(p) !== c);
      const next = palette.length ? palette : s.palette; // never empty
      return { palette: next };
    }),
  setFont: (fontFamily) => set({ fontFamily }),
  setFontSize: (fontSizeRel) => set({ fontSizeRel }),
  setColorSlot: (tool, index, color) =>
    set((s) => {
      const slots = s.colorSlots[tool];
      if (!isStorableColor(color) || index < 0 || index >= slots.length) return s;
      return {
        colorSlots: {
          ...s.colorSlots,
          [tool]: slots.map((c, i) => (i === index ? color : c)),
        },
      };
    }),
  addColorSlot: (tool, color) =>
    set((s) => {
      const slots = s.colorSlots[tool];
      if (!isStorableColor(color) || slots.length >= MAX_COLOR_SLOTS) return s;
      if (slots.some((c) => normHex(c) === normHex(color))) return s;
      return { colorSlots: { ...s.colorSlots, [tool]: [...slots, color] } };
    }),
  setStrokeWidth: (strokeRel) => set({ strokeRel }),
  setHighlighterWidth: (highlighterRel) => set({ highlighterRel }),
  setEraserRadius: (eraserRel) => set({ eraserRel }),
  setPressure: (pressure) => set({ pressure }),
  setSmoothing: (smoothing) => set({ smoothing }),
  setPalmReject: (palmReject) => set({ palmReject }),
  setShapePaletteOpen: (shapePaletteOpen) => set({ shapePaletteOpen }),
  setShapeFill: (shapeFill) => set({ shapeFill }),
  setShapeDash: (shapeDash) => set({ shapeDash }),
  setShapeCorner: (shapeCorner) => set({ shapeCorner: Math.min(0.5, Math.max(0, shapeCorner)) }),
  reset: () => set({ tool: "none" }),
    }),
    {
      // Writing-mode preferences persist across refreshes (colour, pressure,
      // palm rejection, sizes, fonts, palette, and both the last-used and the
      // LIVE tool — see `partialize` for why the live one joined them).
      name: "graphmind.annotation.tool",
      // Serializing 20 keys and writing them to localStorage is SYNCHRONOUS, and
      // the persist middleware ran it on every `set` — including every intermediate
      // value of a colour drag, and every tool switch, on the input path. Coalesce
      // the writes onto a trailing timer and flush on pagehide, so a burst of sets
      // costs one write instead of one each. The read path is untouched, so the
      // stored shape and its migration are unchanged (constraint C10).
      storage: createJSONStorage(() => DEBOUNCED_STORAGE),
      // v1 split `colorSlots` from one shared array into a per-tool record. An
      // install that persisted the old shape would otherwise rehydrate an ARRAY
      // over the record and crash the toolbar on `colorSlots[tool].map` — the
      // hazard of persisting a shape and then changing it. Migrate rather than
      // discard, so a user's kept colours survive as the pen's.
      // v2 moves the default typed-text font to mono. Like the writing-engine
      // default, the stored v1 value is discarded rather than honoured: the old
      // default wrote itself to storage just by opening the app, so an honoured
      // value cannot be told from an inherited one. The font picker (⋯ → Font)
      // is one click if you want another.
      version: 2,
      migrate: (state, from) => {
        const s = (state ?? {}) as Record<string, unknown>;
        if (from < 2) s.fontFamily = "mono";
        if (from >= 1 && !Array.isArray(s.colorSlots)) return s;
        const legacy = Array.isArray(s.colorSlots)
          ? (s.colorSlots as unknown[]).filter(isStorableColor)
          : [];
        return {
          ...s,
          colorSlots: {
            pen: legacy.length ? legacy : DEFAULT_COLOR_SLOTS.pen,
            highlighter: DEFAULT_COLOR_SLOTS.highlighter,
          },
        };
      },
      partialize: (s) => ({
        // The live tool persists: a reload in the middle of writing should hand
        // the pen back, not drop you into read mode. (It deliberately did NOT
        // persist before — a document opened in read mode by design — but that
        // loses your place every refresh, which matters more.)
        tool: s.tool,
        lastTool: s.lastTool,
        lastShape: s.lastShape,
        shapeFill: s.shapeFill,
        shapeDash: s.shapeDash,
        shapeCorner: s.shapeCorner,
        shapePaletteOpen: s.shapePaletteOpen,
        color: s.color,
        fontFamily: s.fontFamily,
        fontSizeRel: s.fontSizeRel,
        strokeRel: s.strokeRel,
        highlighterRel: s.highlighterRel,
        eraserRel: s.eraserRel,
        eraseMode: s.eraseMode,
        pressure: s.pressure,
        smoothing: s.smoothing,
        palmReject: s.palmReject,
        palette: s.palette,
        colorSlots: s.colorSlots,
        toolColor: s.toolColor,
      }),
    },
  ),
);
